/**
 * A random id for one browser, and the funnel events that hang off it.
 *
 * Nine random bytes from `crypto.getRandomValues`, kept in `localStorage`. Not
 * an account and not a device fingerprint — nothing is derived from the machine,
 * so it cannot be recomputed, correlated across sites, or reconstructed after
 * someone clears it.
 *
 * It lives in `localStorage` rather than `sessionStorage` so a returning tester
 * reads as the same person instead of a new one each visit. That is the point:
 * "did anyone come back" is a question a session-per-tab id cannot answer. The
 * cost is that the count now means *browsers*, not visits — and the About
 * dialog says so, because a persistent id that the privacy note does not
 * mention is exactly the kind of thing that erodes trust.
 */

const KEY = 'hladom.id'

function makeId(): string {
  const bytes = new Uint8Array(9)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export const sessionId: string = (() => {
  try {
    const existing = localStorage.getItem(KEY)
    if (existing) return existing
    const fresh = makeId()
    localStorage.setItem(KEY, fresh)
    return fresh
  } catch {
    // Private mode, or storage disabled. A per-load id still groups one visit,
    // and someone who blocks storage should not be worked around.
    return makeId()
  }
})()

import { API_BASE } from './api-base'

export type EventKind = 'land' | 'located' | 'search' | 'walk' | 'arrived'

const sent = new Set<string>()

/**
 * Report a funnel step. Fire-and-forget, and silent on failure — telemetry
 * must never become a visible problem for someone walking in the heat.
 *
 * `once` suppresses repeats within this page load: "walk" should mean *a* walk
 * began, not one event per GPS tick. Deliberately per load rather than per id,
 * so a second visit tomorrow is still counted.
 */
export function track(kind: EventKind, data: Record<string, unknown> = {}, once = false) {
  if (once) {
    if (sent.has(kind)) return
    sent.add(kind)
  }
  try {
    void fetch(`${API_BASE}/api/ev`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, session: sessionId, ...data }),
      keepalive: true,
    }).catch(() => {})
  } catch {
    /* ignore */
  }
}
